/*****************************************************
Naam: TrackingScreen.jsx
Locatie: /Dashboard/src/components/TrackingScreen.jsx
Aangeroepen door: Dashboard.jsx
Datum: 2026-02-13
Versie: 7.0.0 - REDESIGN SIDEBAR
Auteur: Ragnar Standhardt + Claude
******************************************************
Beschrijving:
Pallet tracking voor actieve opdrachten.
- Sidebar: Uitklapbare tracker kaarten met aanvraag info
- Map: Route vanaf laaddatum tot nu
- Real-time updates via Socket.IO
- Aanvraag info in popup (gekoppeld via TrackerID)

CHANGELOG v7.0.0: SIDEBAR REDESIGN
- Nieuw: Compacte kaarten met tracker naam + aanvraagnummer
- Nieuw: Klant, laaddatum, losdatum in compact overzicht
- Nieuw: Uitklapbaar detail paneel met laadadres, losadres,
  goederen, tracker type, IMEI, batterij, laatst gezien
- Fix: Race condition - aanvragen geladen voor posities
- Fix: LaadDatum in toekomst -> filter vanaf vandaag
- Alle trackers standaard geselecteerd op kaart
******************************************************/

import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import io from 'socket.io-client';

// ==================== MARKER ICONS ====================

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const createColoredIcon = (color) => L.divIcon({
  className: '',
  html: '<div style="background:' + color + ';width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -14]
});

// ==================== REVERSE GEOCODING CACHE ====================
const addressCache = new Map();

const getAddress = async (lat, lng) => {
  // Round to 4 decimals (~11m) for cache key
  const key = Math.round(lat * 10000) / 10000 + ',' + Math.round(lng * 10000) / 10000;
  if (addressCache.has(key)) return addressCache.get(key);
  try {
    const res = await fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat=' + lat + '&lon=' + lng + '&zoom=16&addressdetails=1', {
      headers: { 'Accept-Language': 'nl' }
    });
    const data = await res.json();
    const addr = data.address || {};
    const parts = [addr.road, addr.house_number].filter(Boolean).join(' ');
    const city = addr.city || addr.town || addr.village || addr.hamlet || '';
    const result = [parts, city].filter(Boolean).join(', ') || data.display_name?.split(',').slice(0, 3).join(',') || '-';
    addressCache.set(key, result);
    return result;
  } catch (e) {
    return '-';
  }
};

// Tooltip content met adres ophalen
const RouteTooltipContent = ({ pos }) => {
  const [address, setAddress] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    getAddress(pos.latitude, pos.longitude).then(function(addr) {
      if (!cancelled) setAddress(addr);
    });
    return function() { cancelled = true; };
  }, [pos.latitude, pos.longitude]);

  return (
    <div style={{ fontSize: '12px', lineHeight: '1.4', whiteSpace: 'nowrap' }}>
      <div style={{ fontWeight: '700', marginBottom: '2px' }}>
        {pos.gps_timestamp ? new Date(pos.gps_timestamp).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
      </div>
      {pos.speed != null && <div>Snelheid: {Math.round(pos.speed)} km/u</div>}
      <div style={{ color: '#555' }}>{address === null ? 'Laden...' : address}</div>
    </div>
  );
};

// ==================== LINE COLORS ====================

const colorPalette = [
  '#065f46', '#dc2626', '#2563eb', '#f59e0b', '#7c3aed',
  '#10b981', '#ef4444', '#3b82f6', '#f97316', '#a855f7',
  '#14b8a6', '#eab308', '#ec4899', '#6d28d9', '#84cc16',
  '#22c55e', '#f43f5e', '#d946ef', '#8b5cf6', '#6366f1',
  '#4f46e5', '#4338ca', '#3730a3', '#1e293b', '#475569'
];

const getColorForId = (id) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colorPalette[Math.abs(hash) % colorPalette.length];
};

// ==================== HELPERS ====================

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Zojuist';
  if (diffMins < 60) return `${diffMins} min geleden`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)} uur geleden`;
  return date.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// ==================== AUTO FIT BOUNDS ====================
const AutoFitBounds = ({ trackerPositions, selectedTrackers }) => {
  const map = useMap();
  const selectedIds = Array.from(selectedTrackers).sort().join(',');

  useEffect(() => {
    const allPoints = [];
    selectedTrackers.forEach(trackerId => {
      const position = trackerPositions[trackerId];
      if (!position) return;
      if (position.current) {
        allPoints.push([position.current.latitude, position.current.longitude]);
      }
      if (position.history) {
        position.history.forEach(p => allPoints.push(p));
      }
    });

    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, trackerPositions, map]);

  return null;
};

const isLate = (losDatum) => {
  if (!losDatum) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const los = new Date(losDatum); los.setHours(0, 0, 0, 0);
  return today > los;
};

// ==================== DETAIL ROW ====================

const DetailRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
    <span style={{ color: '#888', minWidth: '90px' }}>{label}</span>
    <span style={{ color: color || '#333', fontWeight: color ? '600' : '400', textAlign: 'right', wordBreak: 'break-word', maxWidth: '180px' }}>{value || '-'}</span>
  </div>
);

// ==================== TRACKER CARD ====================

const TrackerCard = ({ tracker, aanvraag, position, isSelected, isExpanded, onToggleSelect, onToggleExpand, lineColor }) => {
  const hasPosition = !!position?.current;
  const late = aanvraag ? isLate(aanvraag.LosDatum) : false;

  return (
    <div style={{
      background: isSelected ? '#f0f7ff' : '#fff',
      border: `2px solid ${isSelected ? '#267dc0' : '#e8e8e8'}`,
      borderRadius: '10px',
      marginBottom: '10px',
      overflow: 'hidden',
      transition: 'all 0.2s',
      boxShadow: isSelected ? '0 2px 8px rgba(38,125,192,0.15)' : '0 1px 3px rgba(0,0,0,0.05)'
    }}>
      {/* COMPACT HEADER */}
      <div style={{ padding: '12px 14px', cursor: 'pointer' }} onClick={onToggleSelect}>
        {/* Regel 1: Checkbox + Tracker naam + Aanvraagnummer + Status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0, accentColor: '#267dc0' }}
            />
            <div style={{
              width: '10px', height: '10px', borderRadius: '50%',
              background: hasPosition ? '#22c55e' : '#f59e0b', flexShrink: 0
            }} />
            <span style={{ fontWeight: '700', fontSize: '14px', color: '#1a1a1a' }}>
              {tracker.device_name || tracker.tracker_id}
            </span>
            {aanvraag && (
              <>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: lineColor, flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#267dc0' }}>
                  {aanvraag.AanvraagID}
                </span>
              </>
            )}
          </div>
          <span style={{
            padding: '3px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
            background: hasPosition ? '#dcfce7' : '#fef3c7',
            color: hasPosition ? '#166534' : '#92400e',
            letterSpacing: '0.5px'
          }}>
            {hasPosition ? 'LIVE' : 'GEEN DATA'}
          </span>
        </div>

        {/* Regel 2: Klant */}
        {aanvraag && (
          <div style={{ fontSize: '13px', color: '#555', marginBottom: '4px', paddingLeft: '18px' }}>
            {aanvraag.Klant || '-'}
          </div>
        )}

        {/* Regel 3: Laaddatum - Losdatum */}
        {aanvraag && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '18px', fontSize: '12px' }}>
            <span style={{ color: '#555' }}>{formatDate(aanvraag.LaadDatum)}</span>
            <span style={{ color: '#bbb' }}>&rarr;</span>
            <span style={{
              color: late ? '#dc2626' : '#555',
              fontWeight: late ? '700' : '400'
            }}>
              {formatDate(aanvraag.LosDatum)}
            </span>
            {late && <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600' }}>Te laat!</span>}
          </div>
        )}

        {!aanvraag && (
          <div style={{ fontSize: '12px', color: '#999', paddingLeft: '18px', fontStyle: 'italic' }}>
            Niet gekoppeld aan aanvraag
          </div>
        )}
      </div>

      {/* EXPAND BUTTON */}
      <div
        style={{
          padding: '6px 14px', background: isExpanded ? '#f0f4f8' : '#f8f9fa',
          borderTop: '1px solid #eee', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '5px', fontSize: '11px', color: '#888', transition: 'all 0.2s'
        }}
        onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
      >
        <span style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>&#9660;</span>
        {isExpanded ? 'Minder info' : 'Meer info'}
      </div>

      {/* EXPANDED DETAILS */}
      {isExpanded && (
        <div style={{ padding: '12px 14px', borderTop: '1px solid #e8e8e8', background: '#fafbfc' }}>
          {aanvraag && (
            <>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#267dc0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Route
              </div>
              <DetailRow label="Laadadres" value={[aanvraag.LaadAdres, aanvraag.LaadPostcode, aanvraag.LaadPlaats].filter(Boolean).join(', ')} />
              <DetailRow label="Laadland" value={aanvraag.LaadLand} />
              <DetailRow label="Losadres" value={[aanvraag.LosAdres, aanvraag.LosPostcode, aanvraag.LosPlaats].filter(Boolean).join(', ')} />
              <DetailRow label="Losland" value={aanvraag.LosLand} />
              <DetailRow label="Status" value={aanvraag.Status} />

              <div style={{ fontSize: '11px', fontWeight: '700', color: '#267dc0', margin: '10px 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Goederen
              </div>
              {aanvraag.Goederen && aanvraag.Goederen.length > 0 ? aanvraag.Goederen.map(function(goed, idx) {
                var soort = goed.Soort || goed.soort || '';
                var omschr = goed.Omschrijving || goed.omschrijving || '';
                if (!soort && !omschr) return null;
                var aantal = goed.Aantal || goed.aantal || '';
                var gewicht = goed.Gewicht || goed.gewicht || '';
                var l = goed.Lengte || goed.lengte || '';
                var b = goed.Breedte || goed.breedte || '';
                var h = goed.Hoogte || goed.hoogte || '';
                var afmeting = (l && b && h) ? l + 'x' + b + 'x' + h + 'm' : '';
                return (
                  <div key={idx} style={{ fontSize: '12px', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                    <div>{aantal}x {soort} {omschr}</div>
                    {(afmeting || gewicht) && (
                      <div style={{ fontSize: '11px', color: '#888', paddingLeft: '16px' }}>
                        {afmeting}{afmeting && gewicht ? ' · ' : ''}{gewicht ? gewicht + ' kg' : ''}
                      </div>
                    )}
                  </div>
                );
              }) : (
                [1, 2, 3, 4, 5].map(function(i) {
                  var aantal = aanvraag['Goed' + i + '_Aantal'];
                  var soort = aanvraag['Goed' + i + '_Soort'] || '';
                  var omschr = aanvraag['Goed' + i + '_Omschrijving'] || '';
                  if (!aantal || (!soort && !omschr)) return null;
                  var gewicht = aanvraag['Goed' + i + '_Gewicht'] || '';
                  var l = aanvraag['Goed' + i + '_Lengte'] || '';
                  var b = aanvraag['Goed' + i + '_Breedte'] || '';
                  var h = aanvraag['Goed' + i + '_Hoogte'] || '';
                  var afmeting = (l && b && h) ? l + 'x' + b + 'x' + h + 'm' : '';
                  return (
                    <div key={i} style={{ fontSize: '12px', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                      <div>{aantal}x {soort} {omschr}</div>
                      {(afmeting || gewicht) && (
                        <div style={{ fontSize: '11px', color: '#888', paddingLeft: '16px' }}>
                          {afmeting}{afmeting && gewicht ? ' · ' : ''}{gewicht ? gewicht + ' kg' : ''}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div style={{ borderTop: '1px dashed #ddd', margin: '10px 0' }} />
            </>
          )}

          <div style={{ fontSize: '11px', fontWeight: '700', color: '#267dc0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Tracker
          </div>
          <DetailRow label="Tracker ID" value={tracker.tracker_id} />
          <DetailRow label="Type" value={tracker.type_name} />
          <DetailRow label="IMEI" value={tracker.imei} />

          {hasPosition && (
            <>
              <div style={{ borderTop: '1px dashed #ddd', margin: '8px 0' }} />
              <DetailRow label="Snelheid" value={position.current.speed ? Math.round(position.current.speed) + ' km/u' : '0 km/u'} />
              <DetailRow label="Batterij" value={position.current.battery_level != null ? position.current.battery_level + '%' : '-'} />
              <DetailRow label="Laatst gezien" value={formatTimestamp(position.current.gps_timestamp)} />
              <DetailRow label="Positie" value={(position.current.latitude ? position.current.latitude.toFixed(5) : '-') + ', ' + (position.current.longitude ? position.current.longitude.toFixed(5) : '-')} />
              {position.history && (
                <DetailRow label="Datapunten" value={position.history.length + ' posities'} />
              )}
            </>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '11px', color: '#888' }}>
            <div style={{ width: '20px', height: '3px', background: lineColor, borderRadius: '2px' }} />
            Route kleur op kaart
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const TrackingScreen = ({ onClose }) => {
  const [allTrackers, setAllTrackers] = useState([]);
  const [selectedTrackers, setSelectedTrackers] = useState(new Set());
  const [expandedTrackers, setExpandedTrackers] = useState(new Set());
  const [trackerPositions, setTrackerPositions] = useState({});
  const [aanvragen, setAanvragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overdueAanvragen, setOverdueAanvragen] = useState([]);
  const [editOverdue, setEditOverdue] = useState(null); // { aanvraagID, losDatum, status }
  const [savingOverdue, setSavingOverdue] = useState(false);
  const [overdueMsg, setOverdueMsg] = useState(null);

  useEffect(() => {
    const init = async () => {
      const loadedAanvragen = await loadAanvragen();
      await loadTrackers(loadedAanvragen);
    };
    init();

    const socket = window.socket || io(
      window.location.hostname === 'localhost'
        ? 'http://localhost:5000'
        : 'https://dashboard.sendwell.nl'
    );
    socket.on('gpsUpdate', (update) => { handleGpsUpdate(update); });
    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrackers = async (aanvragenData) => {
    setLoading(true);
    try {
      const res = await fetch('/api/tracking/trackers');
      const data = await res.json();
      const assignedTrackers = data.filter(t => t.status === 'assigned');
      
      // Filter: alleen trackers tonen waarvan LaadDatum vandaag of in het verleden is
      const vandaag = new Date(); vandaag.setHours(0, 0, 0, 0);
      const activeTrackers = assignedTrackers.filter(t => {
        const aanvraag = (aanvragenData || []).find(a => a.TrackerID === t.tracker_id);
        if (!aanvraag || !aanvraag.LaadDatum) return true; // geen koppeling = tonen
        const laadDatum = new Date(aanvraag.LaadDatum); laadDatum.setHours(0, 0, 0, 0);
        return laadDatum <= vandaag; // alleen tonen als LaadDatum <= vandaag
      });
      
      setAllTrackers(activeTrackers);

      const newSelected = new Set();
      for (const tracker of activeTrackers) {
        await loadTrackerPosition(tracker.tracker_id, aanvragenData);
        newSelected.add(tracker.tracker_id);
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      setSelectedTrackers(newSelected);
    } catch (err) {
      console.error('Error loading trackers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAanvragen = async () => {
    try {
      const res = await fetch('/api/load-aanvragen');
      const result = await res.json();
      if (result.success) {
        const vandaag = new Date(); vandaag.setHours(0, 0, 0, 0);
        const gekoppeld = result.data.filter(a => {
          if (!a.TrackerID) return false;
          const status = (a.Status || '').toLowerCase();
          return status.includes('opdracht') || status.includes('onderweg') || status.includes('bevestigd');
        });
        // Te late aanvragen: tracker + actieve status + losdatum verstreken
        const telaat = result.data.filter(a => {
          if (!a.TrackerID) return false;
          const status = (a.Status || '').toLowerCase();
          if (!status.includes('opdracht') && !status.includes('onderweg') && !status.includes('bevestigd')) return false;
          if (!a.LosDatum) return false;
          const los = new Date(a.LosDatum); los.setHours(0, 0, 0, 0);
          return vandaag > los;
        });
        setOverdueAanvragen(telaat);
        setAanvragen(gekoppeld);
        return gekoppeld;
      }
    } catch (err) {
      console.error('Error loading aanvragen:', err);
    }
    return [];
  };

  const saveOverdue = async () => {
    if (!editOverdue) return;
    setSavingOverdue(true);
    setOverdueMsg(null);
    try {
      const res = await fetch('/api/update-aanvraag-losdatum', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          aanvraagID: editOverdue.aanvraagID,
          losDatum: editOverdue.losDatum,
          status: editOverdue.status
        })
      });
      const data = await res.json();
      if (data.success) {
        setOverdueMsg({ type: 'ok', text: 'Opgeslagen!' });
        setEditOverdue(null);
        // Herlaad aanvragen
        const loadedAanvragen = await loadAanvragen();
        await loadTrackers(loadedAanvragen);
      } else {
        setOverdueMsg({ type: 'err', text: data.error || 'Fout bij opslaan' });
      }
    } catch (e) {
      setOverdueMsg({ type: 'err', text: e.message });
    }
    setSavingOverdue(false);
  };

  const getAanvraagForTracker = (trackerId) => {
    return aanvragen.find(a => a.TrackerID === trackerId);
  };

  const loadTrackerPosition = async (trackerId, aanvragenData) => {
    try {
      const aanvragenList = aanvragenData || aanvragen;
      const aanvraag = aanvragenList.find(a => a.TrackerID === trackerId);
      let url = '/api/tracking/positions/' + trackerId + '?limit=5000';

      if (aanvraag && aanvraag.LaadDatum) {
        const laadDatum = new Date(aanvraag.LaadDatum);
        url += '&from=' + laadDatum.toISOString();
      }

      const res = await fetch(url);
      const positions = await res.json();

      if (positions.length > 0) {
        setTrackerPositions(prev => ({
          ...prev,
          [trackerId]: {
            current: positions[positions.length - 1],
            history: positions.map(p => [p.latitude, p.longitude]),
            fullHistory: positions
          }
        }));
      }
    } catch (err) {
      console.error('Error loading position for ' + trackerId + ':', err);
    }
  };

  const handleGpsUpdate = (update) => {
    const { tracker_id, position } = update;
    setTrackerPositions(prev => {
      const existing = prev[tracker_id] || { history: [] };
      return {
        ...prev,
        [tracker_id]: {
          current: position,
          history: [...existing.history, [position.latitude, position.longitude]]
        }
      };
    });
  };

  const toggleTracker = (trackerId) => {
    setSelectedTrackers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(trackerId)) { newSet.delete(trackerId); } else { newSet.add(trackerId); }
      return newSet;
    });
  };

  const toggleExpand = (trackerId) => {
    setExpandedTrackers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(trackerId)) { newSet.delete(trackerId); } else { newSet.add(trackerId); }
      return newSet;
    });
  };

  const selectedPositions = Array.from(selectedTrackers).map(id => trackerPositions[id]?.current).filter(Boolean);
  const center = selectedPositions.length > 0
    ? [selectedPositions[0].latitude, selectedPositions[0].longitude]
    : [52.3702, 4.8952];
  const activeCount = allTrackers.filter(t => trackerPositions[t.tracker_id]?.current).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
      {/* HEADER */}
      <div style={{
        padding: '16px 24px', background: '#065f46', color: 'white',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0
      }}>
        <div>
          <h2 style={{ margin: 0, marginBottom: '3px', fontSize: '18px' }}>GPS Tracking — Live Monitor</h2>
          <p style={{ margin: 0, fontSize: '13px', opacity: 0.85 }}>
            {activeCount} actief &middot; {selectedTrackers.size} op kaart &middot; {allTrackers.length} toegewezen
            {overdueAanvragen.length > 0 && <span style={{ marginLeft: '8px', background: '#dc2626', padding: '1px 7px', borderRadius: '10px', fontWeight: '700' }}>⚠️ {overdueAanvragen.length} te laat</span>}
          </p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', padding: '4px 8px' }}>
          &#10005;
        </button>
      </div>

      {/* MAIN */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* SIDEBAR */}
        <div style={{ width: '360px', background: '#f5f6f8', borderRight: '2px solid #e0e0e0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', background: '#fff' }}>
            <h3 style={{ margin: 0, fontSize: '15px', color: '#1a1a1a' }}>Zendingen onderweg</h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#888' }}>Klik om op kaart te tonen/verbergen</p>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {/* TE LATE AANVRAGEN sectie */}
            {overdueAanvragen.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ background: '#dc2626', color: 'white', padding: '8px 12px', borderRadius: '6px 6px 0 0', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>⚠️</span> Te laat — losdatum verstreken ({overdueAanvragen.length})
                </div>
                <div style={{ border: '2px solid #dc2626', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
                  {overdueMsg && (
                    <div style={{ padding: '6px 12px', background: overdueMsg.type === 'ok' ? '#dcfce7' : '#fee2e2', color: overdueMsg.type === 'ok' ? '#166534' : '#dc2626', fontSize: '12px', fontWeight: '600' }}>
                      {overdueMsg.text}
                    </div>
                  )}
                  {overdueAanvragen.map(a => (
                    <div key={a.AanvraagID} style={{ padding: '10px 12px', borderBottom: '1px solid #fee2e2', background: '#fff5f5' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                        <div>
                          <span style={{ fontWeight: '700', color: '#dc2626', fontSize: '13px' }}>{a.AanvraagID}</span>
                          <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>{a.Klant || ''}</span>
                        </div>
                        <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600', background: '#fee2e2', padding: '2px 6px', borderRadius: '4px' }}>
                          Los: {a.LosDatum ? new Date(a.LosDatum).toLocaleDateString('nl-NL') : '-'}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
                        {[a.LosPlaats, a.LosLand].filter(Boolean).join(', ')} — Status: <strong>{a.Status}</strong>
                      </div>
                      {editOverdue?.aanvraagID === a.AanvraagID ? (
                        <div style={{ background: '#fff', border: '1px solid #fca5a5', borderRadius: '4px', padding: '8px' }}>
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '120px' }}>
                              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Nieuwe losdatum</div>
                              <input
                                type="date"
                                value={editOverdue.losDatum}
                                onChange={e => setEditOverdue(prev => ({ ...prev, losDatum: e.target.value }))}
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid #ddd', fontSize: '12px', borderRadius: '3px' }}
                              />
                            </div>
                            <div style={{ flex: 1, minWidth: '120px' }}>
                              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Status</div>
                              <select
                                value={editOverdue.status}
                                onChange={e => setEditOverdue(prev => ({ ...prev, status: e.target.value }))}
                                style={{ width: '100%', padding: '4px 6px', border: '1px solid #ddd', fontSize: '12px', borderRadius: '3px', background: 'white' }}
                              >
                                <option value="">— ongewijzigd —</option>
                                <option value="Afgerond">✅ Afgerond</option>
                                <option value="Onderweg">🚚 Onderweg</option>
                                <option value="Vertraging">⏳ Vertraging</option>
                                <option value="Opdracht bevestigd">📋 Opdracht bevestigd</option>
                              </select>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={saveOverdue} disabled={savingOverdue}
                              style={{ flex: 1, padding: '6px', background: '#1a364c', color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                              {savingOverdue ? 'Opslaan...' : '✓ Opslaan'}
                            </button>
                            <button onClick={() => { setEditOverdue(null); setOverdueMsg(null); }}
                              style={{ padding: '6px 10px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '3px', fontSize: '12px', cursor: 'pointer' }}>
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setOverdueMsg(null); setEditOverdue({ aanvraagID: a.AanvraagID, losDatum: '', status: '' }); }}
                          style={{ width: '100%', padding: '5px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '3px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                          ✏️ Bewerken
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>Trackers laden...</div>
            ) : allTrackers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>📦</div>
                <p style={{ margin: 0, fontSize: '14px' }}>Geen actieve trackers</p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#bbb' }}>Wijs trackers toe via Opdrachtbevestiging</p>
              </div>
            ) : (
              allTrackers.map(tracker => {
                const aanvraag = getAanvraagForTracker(tracker.tracker_id);
                const position = trackerPositions[tracker.tracker_id];
                return (
                  <TrackerCard
                    key={tracker.tracker_id}
                    tracker={tracker}
                    aanvraag={aanvraag}
                    position={position}
                    isSelected={selectedTrackers.has(tracker.tracker_id)}
                    isExpanded={expandedTrackers.has(tracker.tracker_id)}
                    onToggleSelect={() => toggleTracker(tracker.tracker_id)}
                    onToggleExpand={() => toggleExpand(tracker.tracker_id)}
                    lineColor={getColorForId(tracker.tracker_id)}
                  />
                );
              })
            )}
          </div>

          <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0', background: '#fff' }}>
            <button
              onClick={async () => { const a = await loadAanvragen(); await loadTrackers(a); }}
              disabled={loading}
              style={{
                width: '100%', padding: '10px', background: loading ? '#ccc' : '#267dc0',
                color: 'white', border: 'none', borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px'
              }}
            >
              {loading ? 'Laden...' : 'Vernieuwen'}
            </button>
          </div>
        </div>

        {/* MAP */}
        <div style={{ flex: 1 }}>
          <MapContainer center={center} zoom={7} style={{ width: '100%', height: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
            <AutoFitBounds trackerPositions={trackerPositions} selectedTrackers={selectedTrackers} />

            {Array.from(selectedTrackers).map(trackerId => {
              const position = trackerPositions[trackerId];
              if (!position?.current) return null;

              const lineColor = getColorForId(trackerId);
              const current = position.current;
              const aanvraag = getAanvraagForTracker(trackerId);
              const tracker = allTrackers.find(t => t.tracker_id === trackerId);

              const today = new Date(); today.setHours(0, 0, 0, 0);
              const gepland = aanvraag && aanvraag.LosDatum ? new Date(aanvraag.LosDatum) : null;
              if (gepland) gepland.setHours(0, 0, 0, 0);
              const teLaat = gepland && today > gepland;

              return (
                <React.Fragment key={trackerId}>
                  {position.history.length > 1 && (
                    <Polyline positions={position.history} color={lineColor} weight={4} opacity={0.8} />
                  )}
                  {/* Route punten met tooltip bij hover */}
                  {position.fullHistory && position.fullHistory.map(function(pos, idx) {
                    return (
                      <CircleMarker
                        key={idx}
                        center={[pos.latitude, pos.longitude]}
                        radius={4}
                        pathOptions={{ color: lineColor, fillColor: lineColor, fillOpacity: 0.7, weight: 1, opacity: 0 }}
                        eventHandlers={{ mouseover: function(e) { e.target.setStyle({ opacity: 1, radius: 6 }); e.target.openTooltip(); }, mouseout: function(e) { e.target.setStyle({ opacity: 0, radius: 4 }); e.target.closeTooltip(); } }}
                      >
                        <Tooltip direction="top" offset={[0, -8]} className="route-tooltip">
                          <RouteTooltipContent pos={pos} />
                        </Tooltip>
                      </CircleMarker>
                    );
                  })}
                  <Marker position={[current.latitude, current.longitude]} icon={createColoredIcon(lineColor)}>
                    <Popup>
                      <div style={{ minWidth: '260px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <strong style={{ fontSize: '15px' }}>{tracker?.device_name || trackerId}</strong>
                          {aanvraag && <span style={{ fontSize: '14px', fontWeight: '700', color: '#267dc0' }}>{aanvraag.AanvraagID}</span>}
                        </div>
                        <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px solid #eee' }} />
                        <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                          {aanvraag && (
                            <>
                              <div><strong>Klant:</strong> {aanvraag.Klant || '-'}</div>
                              <div><strong>Van:</strong> {[aanvraag.LaadPlaats, aanvraag.LaadLand].filter(Boolean).join(', ')}</div>
                              <div><strong>Naar:</strong> {[aanvraag.LosPlaats, aanvraag.LosLand].filter(Boolean).join(', ')}</div>
                              <div>
                                <strong>Goederen:</strong>
                                {aanvraag.Goederen && aanvraag.Goederen.length > 0 ? aanvraag.Goederen.map(function(goed, idx) {
                                  var soort = goed.Soort || goed.soort || '';
                                  var omschr = goed.Omschrijving || goed.omschrijving || '';
                                  if (!soort && !omschr) return null;
                                  var aantal = goed.Aantal || goed.aantal || '';
                                  var gewicht = goed.Gewicht || goed.gewicht || '';
                                  var l = goed.Lengte || goed.lengte || '';
                                  var b = goed.Breedte || goed.breedte || '';
                                  var h = goed.Hoogte || goed.hoogte || '';
                                  var afmeting = (l && b && h) ? ' (' + l + 'x' + b + 'x' + h + 'm' + (gewicht ? ', ' + gewicht + ' kg' : '') + ')' : (gewicht ? ' (' + gewicht + ' kg)' : '');
                                  return (
                                    <div key={idx} style={{ paddingLeft: '8px', fontSize: '12px', color: '#555' }}>
                                      {aantal}x {soort} {omschr}{afmeting}
                                    </div>
                                  );
                                }) : (
                                  [1, 2, 3, 4, 5].map(function(i) {
                                    var aantal = aanvraag['Goed' + i + '_Aantal'];
                                    var soort = aanvraag['Goed' + i + '_Soort'] || '';
                                    var omschr = aanvraag['Goed' + i + '_Omschrijving'] || '';
                                    if (!aantal || (!soort && !omschr)) return null;
                                    var gewicht = aanvraag['Goed' + i + '_Gewicht'] || '';
                                    var l = aanvraag['Goed' + i + '_Lengte'] || '';
                                    var b = aanvraag['Goed' + i + '_Breedte'] || '';
                                    var h = aanvraag['Goed' + i + '_Hoogte'] || '';
                                    var afmeting = (l && b && h) ? ' (' + l + 'x' + b + 'x' + h + 'm' + (gewicht ? ', ' + gewicht + ' kg' : '') + ')' : (gewicht ? ' (' + gewicht + ' kg)' : '');
                                    return (
                                      <div key={i} style={{ paddingLeft: '8px', fontSize: '12px', color: '#555' }}>
                                        {aantal}x {soort} {omschr}{afmeting}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                              <hr style={{ margin: '5px 0', border: 'none', borderTop: '1px dashed #ddd' }} />
                              <div><strong>Geplande aankomst:</strong> {gepland ? gepland.toLocaleDateString('nl-NL') : '-'}</div>
                              <div>
                                <strong>Vandaag:</strong>{' '}
                                <span style={{ color: teLaat ? '#dc2626' : '#000', fontWeight: teLaat ? '700' : '400' }}>
                                  {today.toLocaleDateString('nl-NL')}{teLaat ? ' - Te laat!' : ''}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })}
          </MapContainer>
        </div>
      </div>
    </div>
  );
};

export default TrackingScreen;
